import { useEffect, useRef, useState } from "react";
import { Send, Paperclip, Loader2, Search, MessageSquare, ArrowRight, ArrowLeft, X, Languages } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { api, formatApiError } from "../lib/api";

const MAX_IMAGES = 6;
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
async function fileToDataUrl(f) {
    return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
}

/** Admin panel: list of conversations + active-thread view. */
export default function AdminChat() {
    const navigate = useNavigate();
    const [threads, setThreads] = useState([]);
    const [search, setSearch] = useState("");
    const [active, setActive] = useState(null);  // thread doc OR { company_id, company_name } to initiate
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [images, setImages] = useState([]);
    const [sending, setSending] = useState(false);
    const [starting, setStarting] = useState(false);
    const [translating, setTranslating] = useState(null);
    const [showOriginal, setShowOriginal] = useState({});
    const [allCompanies, setAllCompanies] = useState([]);
    const [showInitiator, setShowInitiator] = useState(false);
    const fileRef = useRef(null);
    const scrollRef = useRef(null);

    const loadThreads = async () => {
        const r = await api.get("/admin/chat/threads");
        setThreads(r.data.items || []);
    };

    const loadCompanies = async () => {
        try {
            const r = await api.get("/admin/accounts");
            // Only companies (not employees) can hold threads at this level.
            const items = (r.data?.items || r.data || []).filter((c) => c.account_type !== "employee");
            setAllCompanies(items);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        loadThreads();
        loadCompanies();
        const id = setInterval(() => { if (document.visibilityState === "visible") loadThreads(); }, 15000);
        return () => clearInterval(id);
    }, []);

    const openThread = async (companyId) => {
        try {
            const r = await api.get(`/admin/chat/threads/${companyId}`);
            setActive(r.data.thread);
            setMessages(r.data.messages || []);
            setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 80);
            loadThreads();  // refresh unread
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const initiateWith = async (companyId, companyName) => {
        setStarting(true);
        try {
            await openThread(companyId);
            setShowInitiator(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setStarting(false); }
    };

    const onFiles = async (files) => {
        const picked = Array.from(files || []);
        const room = MAX_IMAGES - images.length;
        if (room <= 0) return toast.error(`الحد ${MAX_IMAGES} صور`);
        const slice = picked.slice(0, room);
        const next = [...images];
        for (const f of slice) {
            if (f.size > MAX_IMAGE_BYTES) { toast.error(`${f.name}: الحد 2MB`); continue; }
            try { next.push(await fileToDataUrl(f)); } catch {}
        }
        setImages(next);
    };

    const send = async () => {
        if (!active) return;
        const t = text.trim();
        if (!t && images.length === 0) return;
        setSending(true);
        try {
            await api.post(`/admin/chat/threads/${active.company_id}/messages`, { text: t, images });
            setText(""); setImages([]);
            await openThread(active.company_id);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSending(false); }
    };

    const translate = async (msg) => {
        const hasAr = /[\u0600-\u06FF]/.test(msg.text || "");
        const target = hasAr ? "en" : "ar";
        setTranslating(msg.id);
        try {
            const r = await api.post(`/chat/messages/${msg.id}/translate`, { target_lang: target });
            setMessages((cur) => cur.map((m) => (m.id === msg.id ? { ...m, translated_text: r.data.translated_text, translated_lang: r.data.translated_lang } : m)));
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setTranslating(null); }
    };

    const filtered = threads.filter((t) => (t.company_name || "").toLowerCase().includes(search.toLowerCase()));
    const availableCompanies = allCompanies.filter((c) => !threads.some((t) => t.company_id === c.id));

    return (
        <div className="min-h-screen bg-neutral-50 p-4" dir="rtl" data-testid="admin-chat">
            <div className="mx-auto flex max-w-6xl flex-col gap-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/accounts")} className="gap-1" data-testid="admin-chat-back-home">
                            <ArrowRight className="h-4 w-4" />
                            الرئيسية
                        </Button>
                        <h1 className="font-heading text-2xl font-bold text-neutral-900">إدارة المحادثات</h1>
                    </div>
                    <Button onClick={() => setShowInitiator(true)} className="gap-1 bg-amber-700 text-white hover:bg-amber-800" data-testid="admin-new-chat-btn">
                        <MessageSquare className="h-4 w-4" /> بدء محادثة جديدة
                    </Button>
                </div>

                <div className="flex gap-3">
                    {/* List */}
                    <aside className="w-80 shrink-0 rounded-2xl bg-white p-3 ring-1 ring-neutral-200" style={{ maxHeight: "78vh", overflowY: "auto" }}>
                        <div className="relative">
                            <Search className="absolute inset-y-0 end-3 m-auto h-4 w-4 text-neutral-400" />
                            <Input placeholder="بحث..." value={search} onChange={(e) => setSearch(e.target.value)} className="pe-9" data-testid="admin-chat-search" />
                        </div>
                        <div className="mt-2 space-y-1">
                            {filtered.length === 0 ? (
                                <div className="py-10 text-center text-sm text-neutral-500">لا توجد محادثات</div>
                            ) : (
                                filtered.map((t) => (
                                    <button
                                        key={t.id}
                                        onClick={() => openThread(t.company_id)}
                                        className={`w-full rounded-lg px-3 py-2 text-start transition hover:bg-neutral-100 ${active?.id === t.id ? "bg-neutral-100" : ""}`}
                                        data-testid={`admin-thread-${t.company_id}`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="truncate font-semibold text-neutral-900">{t.company_name}</span>
                                            {t.unread_for_admin > 0 ? (
                                                <span className="rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{t.unread_for_admin}</span>
                                            ) : null}
                                        </div>
                                        <div className="mt-0.5 truncate text-xs text-neutral-500">{t.last_message || "—"}</div>
                                        {t.last_message_at ? (
                                            <div className="mt-0.5 text-[10px] text-neutral-400">{new Date(t.last_message_at).toLocaleString("ar-SA")}</div>
                                        ) : null}
                                    </button>
                                ))
                            )}
                        </div>
                    </aside>

                    {/* Thread */}
                    <section className="flex min-h-[78vh] flex-1 flex-col rounded-2xl bg-white ring-1 ring-neutral-200">
                        {!active ? (
                            <div className="m-auto max-w-xs text-center text-sm text-neutral-500">
                                اختر محادثة من اليمين، أو ابدأ محادثة جديدة مع أي حساب.
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center justify-between gap-2 border-b border-neutral-200 px-5 py-3">
                                    <div className="font-heading text-lg font-bold text-neutral-900" data-testid="admin-thread-title">{active.company_name}</div>
                                    <Button variant="ghost" size="sm" onClick={() => setActive(null)} className="gap-1 shrink-0" data-testid="admin-thread-back">
                                        <ArrowRight className="h-4 w-4" />
                                        قائمة المحادثات
                                    </Button>
                                </div>
                                <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto bg-neutral-50 p-4" style={{ maxHeight: "55vh" }}>
                                    {messages.length === 0 ? (
                                        <div className="pt-16 text-center text-sm text-neutral-500">لا توجد رسائل بعد</div>
                                    ) : (
                                        messages.map((m) => {
                                            const mine = m.sender === "admin";
                                            const show = showOriginal[m.id];
                                            return (
                                                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                                    <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 shadow-sm ${mine ? "bg-amber-700 text-white" : "bg-white text-neutral-900 ring-1 ring-neutral-200"}`}>
                                                        {m.text ? (
                                                            <div className="whitespace-pre-wrap break-words text-sm">
                                                                {m.translated_text && !show ? m.translated_text : m.text}
                                                            </div>
                                                        ) : null}
                                                        {m.images && m.images.length > 0 ? (
                                                            <div className="mt-2 grid grid-cols-2 gap-1.5">
                                                                {m.images.map((src, i) => (
                                                                    <a key={i} href={src} target="_blank" rel="noreferrer">
                                                                        <img src={src} alt="" className="h-28 w-full rounded object-cover ring-1 ring-black/5" />
                                                                    </a>
                                                                ))}
                                                            </div>
                                                        ) : null}
                                                        <div className={`mt-1 flex items-center justify-between gap-2 text-[10px] ${mine ? "text-amber-100" : "text-neutral-500"}`}>
                                                            <span>{new Date(m.created_at).toLocaleString("ar-SA")}</span>
                                                            {m.text ? (
                                                                m.translated_text ? (
                                                                    <button onClick={() => setShowOriginal((s) => ({ ...s, [m.id]: !s[m.id] }))} className="font-bold hover:underline">
                                                                        {show ? "عرض الترجمة" : "عرض الأصل"}
                                                                    </button>
                                                                ) : (
                                                                    <button onClick={() => translate(m)} disabled={translating === m.id} className="inline-flex items-center gap-1 font-bold hover:underline">
                                                                        {translating === m.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Languages className="h-3 w-3" />}
                                                                        ترجمة
                                                                    </button>
                                                                )
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                                {images.length > 0 ? (
                                    <div className="flex flex-wrap gap-2 border-t border-neutral-200 bg-white p-2">
                                        {images.map((src, i) => (
                                            <div key={i} className="relative">
                                                <img src={src} alt="" className="h-14 w-14 rounded object-cover ring-1 ring-neutral-200" />
                                                <button onClick={() => setImages((c) => c.filter((_, j) => j !== i))} className="absolute -end-1 -top-1 rounded-full bg-red-600 p-0.5 text-white">
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                                <div className="flex items-end gap-2 border-t border-neutral-200 bg-white p-2">
                                    <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => onFiles(e.target.files)} />
                                    <Button variant="ghost" size="icon" onClick={() => fileRef.current?.click()} data-testid="admin-attach-btn">
                                        <Paperclip className="h-5 w-5 text-neutral-600" />
                                    </Button>
                                    <Textarea
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        placeholder="اكتب الرد..."
                                        rows={1}
                                        className="min-h-[42px] flex-1 resize-none border-0 shadow-none focus-visible:ring-0"
                                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                                        data-testid="admin-chat-input"
                                    />
                                    <Button onClick={send} disabled={sending || (!text.trim() && images.length === 0)} className="h-10 gap-1 bg-amber-700 text-white hover:bg-amber-800" data-testid="admin-send-btn">
                                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                        إرسال
                                    </Button>
                                </div>
                            </>
                        )}
                    </section>
                </div>
            </div>

            {showInitiator ? (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4" onClick={() => setShowInitiator(false)}>
                    <div className="w-full max-w-md rounded-2xl bg-white p-5 ring-1 ring-neutral-200" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h3 className="font-heading text-lg font-bold">بدء محادثة مع شركة</h3>
                            <button onClick={() => setShowInitiator(false)} className="rounded-full p-1 hover:bg-neutral-100"><X className="h-4 w-4" /></button>
                        </div>
                        <p className="mt-1 text-xs text-neutral-500">اختر حساباً لا توجد معه محادثة سابقة.</p>
                        <div className="mt-3 max-h-80 space-y-1 overflow-y-auto">
                            {availableCompanies.length === 0 ? (
                                <div className="py-6 text-center text-xs text-neutral-500">لا توجد حسابات أخرى</div>
                            ) : (
                                availableCompanies.map((c) => (
                                    <button
                                        key={c.id}
                                        onClick={() => initiateWith(c.id, c.company_name)}
                                        disabled={starting}
                                        className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-neutral-50"
                                        data-testid={`admin-start-${c.id}`}
                                    >
                                        <span>{c.company_name}</span>
                                        <ArrowRight className="h-3.5 w-3.5 text-neutral-400" />
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
